'use client';

import { GraphqlBoxProvider } from '@graphql-box/react';
import { CssBaseline, Link as MuiLink, ThemeProvider, createTheme, styled } from '@mui/material';
import { BreadcrumbProvider } from '@next-box/breadcrumb';
import { EnvsProvider } from '@next-box/envs';
import { I18nProvider } from '@next-box/i18n/client';
import { LinkProvider } from '@next-box/link-extra';
import NextLink from 'next/link.js';
import { usePathname, useSearchParams } from 'next/navigation.js';
import { type ReactNode, StrictMode, useEffect, useMemo } from 'react';
import { LinkLoading } from '#components/LinkLoading/index.tsx';
import { Provider as ToolbeltProvider } from '#contexts/Toolbelt/index.ts';
import * as componentMapper from '#i18n/componentMapper.tsx';
import { useWorker } from '#libs/graphqlBox/useWorker.ts';
import { createWorkerClient } from '#libs/graphqlBox/workerClient.ts';
import { StoreProvider } from '#store/StoreProvider.tsx';
import { themeOverrides } from '#themeOverrides.ts';
import { type Config } from '#types.ts';
import { createRouteRules } from '../Breadcrumb/createRouteRules.ts';
import { FeatureFlagProviderWrapper } from '../FeatureFlagProviderWrapper/index.tsx';

const Container = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
}));

export type AppScaffoldProps = {
  children: ReactNode;
  config: Config;
  envs: Record<string, string | undefined>;
};

export const AppScaffold = ({ children, config, envs }: Readonly<AppScaffoldProps>) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const graphqlBoxClient = useMemo(() => createWorkerClient(), []);
  const worker = useWorker();

  useEffect(() => {
    if (worker) {
      graphqlBoxClient.worker = worker;
      graphqlBoxClient.cache.worker = worker;
    }
  }, [graphqlBoxClient, worker]);

  return (
    <StrictMode>
      <EnvsProvider envs={envs}>
        <FeatureFlagProviderWrapper envs={envs}>
          <StoreProvider>
            <GraphqlBoxProvider graphqlBoxClient={graphqlBoxClient}>
              <ToolbeltProvider config={config}>
                <I18nProvider componentMapper={componentMapper}>
                  <LinkProvider
                    LoadingComponent={LinkLoading}
                    NextLink={NextLink}
                    OwnLink={MuiLink}
                    pathname={pathname}
                  >
                    <BreadcrumbProvider
                      currentPathname={pathname}
                      routeRules={createRouteRules({ client: graphqlBoxClient })}
                      search={searchParams.toString()}
                    >
                      <ThemeProvider theme={createTheme(themeOverrides)}>
                        <Container>
                          <CssBaseline />
                          {children}
                        </Container>
                      </ThemeProvider>
                    </BreadcrumbProvider>
                  </LinkProvider>
                </I18nProvider>
              </ToolbeltProvider>
            </GraphqlBoxProvider>
          </StoreProvider>
        </FeatureFlagProviderWrapper>
      </EnvsProvider>
    </StrictMode>
  );
};
